import { PropertyViewModel } from '../property-view-model';
import { PropertyViewModelInitializationInfo } from '../property-view-model-initialization-info';
import { NumericMetaData } from '../../meta-data/numeric-meta-data';
import { NtsMaskedNumber, NumericHelper, NumericMaskSettings } from '@nts/std/utility';
import { takeUntil } from 'rxjs/operators';
import { BaseValidator } from '../../domain-models/decorators/commons/base-validator';
import { ValidationArguments } from 'class-validator';
import { NumberValidator } from '../../domain-models/decorators/number.decorator';
import { ValidationErrorCodes } from '../../resources/validation-error-codes';
import { RangeValidator } from '../../domain-models/decorators/validations/range-validation';
import { BigNumber } from '@nts/std/types';
import type { BaseErrorInterface, ModelInterface, NumericPropertyViewModelInterface } from '@nts/std/interfaces';
import { BaseError } from '@nts/std/messages';

export abstract class BaseNumericPropertyViewModel<TModelType = BigNumber | number | null> extends PropertyViewModel<TModelType> implements NumericPropertyViewModelInterface<TModelType> {

  isDecimal = false;

  private internalMinValue: TModelType = null;

  /**
   * Se impostato agisce direttamente sull'input senza consentire l'inserimento di un valore più basso di quello definito
   */
  get minValue(): TModelType {
    return this.internalMinValue;
  }
  set minValue(value: TModelType) {
    if (this.internalMinValue !== value) {
      this.internalMinValue = value;
      this.onPropertyChanged('minValue');
    }
  }

  protected bigNumber = BigNumber.clone({
    EXPONENTIAL_AT: [-200, 200],
    DECIMAL_PLACES: 0
  }) as any;

  private internalMinValueValidation: null | number | BigNumber = null;

  /**
   * Se impostato visualizza un errore se viene inserito un valore più basso di quello definito
   * Non viene considerato se è già stata impostata la property minValue
   */
  get minValueValidation(): number | BigNumber | null {
    return this.internalMinValueValidation;
  }
  set minValueValidation(value: number | BigNumber | null) {
    if (this.internalMinValueValidation !== value) {
      this.internalMinValueValidation = value;
      this.validate();
      this.onPropertyChanged('minValueValidation');
    }
  }

  private internalMaxValue: TModelType = null;

  /**
   * Se impostato agisce direttamente sull'input senza consentire l'inserimento di un valore più alto di quello definito
   */
  get maxValue(): TModelType {
    return this.internalMaxValue;
  }
  set maxValue(value: TModelType) {
    if (this.internalMaxValue !== value) {
      this.internalMaxValue = value;
      this.onPropertyChanged('maxValue');
    }
  }

  private internalDecimalLimit: null | number = null;

  /**
   * Limite di decimali massimi
   */
  get decimalLimit(): number | null {
    return this.internalDecimalLimit;
  }
  set decimalLimit(value: number) {
    if (this.internalDecimalLimit !== value) {
      this.internalDecimalLimit = value;
      this.onPropertyChanged('decimalLimit');
    }
  }

  private internalIntegerLimit: null | number = null;

  /**
   * Limite di interi massimi
   */
  get integerLimit(): number | null {
    return this.internalIntegerLimit;
  }
  set integerLimit(value: number) {
    if (this.internalIntegerLimit !== value) {
      this.internalIntegerLimit = value;
      this.onPropertyChanged('integerLimit');
    }
  }

  private internalIsBigNumber: boolean = false;

  /**
   * E' un BigNumber
   */
  get isBigNumber(): boolean {
    return this.internalIsBigNumber;
  }
  set isBigNumber(value: boolean) {
    if (this.internalIsBigNumber !== value) {
      this.internalIsBigNumber = value;
      this.onPropertyChanged('isBigNumber');
    }
  }

  private internalMaxValueValidation: null | number | BigNumber = null;

  /**
   * Se impostato visualizza un errore se viene inserito un valore più basso di quello definito
   * Non viene considerato se è già stata impostata la property maxValue
   */
  get maxValueValidation(): number | BigNumber | null {
    return this.internalMaxValueValidation;
  }
  set maxValueValidation(value: number | BigNumber | null) {
    if (this.internalMaxValueValidation !== value) {
      this.internalMaxValueValidation = value;
      this.validate();
      this.onPropertyChanged('maxValueValidation');
    }
  }

  private internalUseThousandSeparator = false;

  override get value(): TModelType {

    if (this.isBigNumber) {
      // Casistica in cui abbiamo il decoratore custom property view model impostato
      if (this._isCustom && this.customGetter == null) {
        throw new Error('You must implement value getter');
      }
      // Casistica in cui viene ridefinito il custom getter
      // Di default viene definito in tutti gli external view model
      const value = this.getValue();

      if (this.value$ && this.value$.value == null && value != null) {
        // Se uno dei due valori è Null e l'altro no
        this.value$.next(value);
      } else if (this.value$ && this.value$.value != null && value == null) {
        // Se uno dei due valori è Null e l'altro no
        this.value$.next(value);
      } else {
        // Per fare la comparazione devo trasformare entrambi i valori in bignumber
        let bigNumber1 = this.value$ ? this.value$.value : null;
        if (!(bigNumber1 instanceof BigNumber)) {
          bigNumber1 = new BigNumber(bigNumber1 as number) as TModelType;
        }
        let bigNumber2 = value;
        if (!(bigNumber2 instanceof BigNumber)) {
          bigNumber2 = new BigNumber(bigNumber2 as number) as TModelType;
        }
        if (!BigNumber.compareValues(bigNumber1 as BigNumber, bigNumber2 as BigNumber)) {
          this.value$.next(value);
        }
      }
      return value;
    } else {
      return super.value;
    }
  }

  override compareValues(v1: TModelType, v2: TModelType, operator: string = '=='): boolean {
    return BigNumber.compareValues(v1 as BigNumber, v2 as BigNumber, operator);
  }

  override set value(updatedValue: TModelType) {
    this.setValueAsync(updatedValue);
  }

  override getValue(): TModelType {
    const value = super.getValue();
    return this.isBigNumber ?
      (value != null ? this.bigNumber(value) : null as TModelType) :
      (value ?? null) as TModelType;
  }

  get useThousandSeparator(): boolean {
    return this.internalUseThousandSeparator;
  }
  set useThousandSeparator(value: boolean) {
    if (this.internalUseThousandSeparator !== value) {
      this.internalUseThousandSeparator = value;
      this.onPropertyChanged('useThousandSeparator');
    }
  }

  private internalCustomMaskSettings = null;

  get customMaskSettings(): (defaultMaskSettings: NumericMaskSettings) => NumericMaskSettings {
    return this.internalCustomMaskSettings;
  }
  set customMaskSettings(value: (defaultMaskSettings: NumericMaskSettings) => NumericMaskSettings) {
    if (this.internalCustomMaskSettings !== value) {
      this.internalCustomMaskSettings = value;
      this.onPropertyChanged('customMaskSettings');
    }
  }

  /** @deprecated nei numerici non personalizzare il customFormattedValueFunc ma usare il customMaskSettings */
  override get customFormattedValueFunc() {
    return super.customFormattedValueFunc;
  }
  /** @deprecated nei numerici non personalizzare il customFormattedValueFunc ma usare il customMaskSettings */
  override set customFormattedValueFunc(val: ((updatedValue: TModelType) => string) | null) {
    super.customFormattedValueFunc = val;
  }

  getMaskedValue(value: TModelType): string {
    return BaseNumericPropertyViewModel.getMaskedFormattedValue(this);
  }

  getFormattedValue(value: TModelType): string {
    if (this.customFormattedValueFunc) {
      return this.customFormattedValueFunc(value);
    }
    return this.getMaskedValue(value);
  }

  constructor(
    initInfo: PropertyViewModelInitializationInfo,
    valueIsNullable: boolean
  ) {
    super(initInfo, true);

    const metaData = initInfo.propertyMetaData as NumericMetaData;
    this.isDecimal = metaData.isDecimal;

    let maxValue = this.maxValue;
    let minValue = this.minValue;
    let decimalLimit = this.decimalLimit;
    let integerLimit = this.integerLimit;
    let useThousandSeparator = this.useThousandSeparator;

    if (this.isDecimal === false) {
      maxValue = NumericHelper.MAX_VALUE_INT32 as TModelType;
      minValue = NumericHelper.MIN_VALUE_INT32 as TModelType;
      decimalLimit = 0;
      integerLimit = (metaData?.maxIntegerPrecision && (metaData?.maxIntegerPrecision > 0))
        ? metaData.maxIntegerPrecision
        : NumericHelper.MAX_VALUE_INT32.toString().length;
    } else {
      decimalLimit = NumericHelper.getDecimalDigits(
        this.propertyName,
        this.integerLimit,
        metaData?.maxDecimalPrecision ?? null
      );
      integerLimit = NumericHelper.getIntegerDigits(
        this.propertyName,
        metaData?.maxIntegerPrecision ?? null,
        metaData?.maxDecimalPrecision ?? null
      );
    }

    if (decimalLimit > 0) {
      useThousandSeparator = true;
    }

    this.bigNumber = BigNumber.clone({
      EXPONENTIAL_AT: [-200, 200],
      DECIMAL_PLACES: decimalLimit ?? 0
    });

    this.isBigNumber = (decimalLimit + integerLimit) >= NumericHelper.MIN_DIGIT_FOR_BIGNUMBER;

    this.minValueValidation = metaData.minValue ?? null;
    this.maxValueValidation = metaData.maxValue ?? null;
    this.useThousandSeparator = useThousandSeparator;
    this.decimalLimit = decimalLimit;
    this.maxValue = maxValue;
    this.minValue = minValue;
    this.integerLimit = integerLimit;

    this.updateFormattedValue();

    this.propertyChanged.pipe(takeUntil(this.destroySubscribers$)).subscribe((args) => {
      if (args.propertyName == 'useThousandSeparator') {
        this.updateFormattedValue();
      }
    });
  }

  override getModelErrors(propertyName: string): BaseErrorInterface[] {

    const allPropertyErrors: BaseErrorInterface[] = super.getModelErrors(propertyName);

    const minValue = this.minValueValidation ?? this.minValue;
    const maxValue = this.maxValueValidation ?? this.maxValue;

    const hasValidValues = this.value != null
      && (
        minValue != null
        || maxValue != null
      );

    // Se è valorizzata livello di pvm
    if (hasValidValues) {
      // Pulisco gli errori aggiunti dal domain model (quelli derivanti dai decoratori)
      const cleanedPropertyErrors = allPropertyErrors.filter(
        (propertyError) => propertyError.code !== ValidationErrorCodes.NumberMustBeGreater &&
          propertyError.code !== ValidationErrorCodes.NumberMustBeLess &&
          propertyError.code !== ValidationErrorCodes.NumberOutOfRange
      );

      const validator = new RangeValidator(minValue, maxValue, this.metadataShortDescription);

      if (!validator.validate(this.value)) {
        const e: BaseErrorInterface = new BaseError();
        e.description = validator.errorMessage;
        e.code = validator.messageCode;
        e.propertyName = this.propertyName;
        cleanedPropertyErrors.push(e);
      }

      return cleanedPropertyErrors;
    }

    return allPropertyErrors;
  }

  parseFromString(value: string): number {

    const mask = new NtsMaskedNumber({
      mask: Number,
      scale: this.decimalLimit ? this.decimalLimit : null,
      signed: false,
      padFractionalZeros: true,
      normalizeZeros: true,
      overwrite: 'shift',
      max: this.maxValue,

      min: this.minValue,
      thousandsSeparator: this.useThousandSeparator ? NumericHelper.getThousandSeparator() : '',
      radix: NumericHelper.getDecimalSeparator(),
      mapToRadix: [NumericHelper.getDecimalSeparator()],
      nullable: true
    } as any);

    mask.value = value;
    return mask.typedValue;
  }

  override validateCustomPropertyViewModel(baseErrors: BaseErrorInterface[]): boolean {
    let result = true;
    if (this.parent) {
      const propertyMetaData: NumericMetaData = BaseValidator.getPropertyMetaData(this.parent, this.propertyName);

      const args: Partial<ValidationArguments> = {
        constraints: [NumberValidator.getDecoratorDataFromPropertyMetaData(propertyMetaData)],
        object: this.parent,
        property: this.propertyName
      };

      const validator = new NumberValidator<TModelType>();
      result = validator.validate(this.value, args as any);

      if (result === false) {
        const messageError = new BaseError();
        messageError.propertyName = this.propertyName;
        messageError.objectName = this.parent.classType;
        messageError.description = validator.errorMessage;
        messageError.code = 'VALIDATION_ERROR';
        baseErrors.push(messageError);
      }
    }

    return result;
  }

  override async setValueAsync(updatedValue: TModelType) {
    if (this._isCustom && this.customSetter == null) {
      throw new Error('You must implement value setter');
    }
    if (!this.isNullable) {
      if (updatedValue == null) {
        updatedValue = this.faultBackValue;
      }
    }
    if (this.isBigNumber && updatedValue != null) {
      updatedValue = this.bigNumber(updatedValue);
    }

    if (
      this.isBigNumber ?
        !BigNumber.compareValues(this.value as BigNumber, updatedValue as BigNumber, "==") :
        (this.value !== updatedValue)
    ) {
      if (this.customSetter != null) {
        await this.customSetter(updatedValue);
        if (this.isCustom) {
          this.validate();
        } else if (this.model != null) {
          this.validate();
        } else if (this.parent != null && this.parent.validate) {
          this.parent.validate();
        }
        this.onPropertyChanged(this.bindedValuePropertyName);
      } else {
        // quano faccio il sync model scatta onPropertyChanged partendo dal modello
        this.syncModel(updatedValue);
        this.validate();
      }
      // TODO verificare se si può rimuovere
      this.formattedValue$.next(this.formattedValue);

      // Aggiorno il valore per gli ascoltatori
      this.value$.next(this.value);
    }
  }

  override setModel(model: ModelInterface) {
    super.setModel(model);
  }

  override async resetValue(useDefaultValue: boolean): Promise<void> {
    const resetValue = (useDefaultValue ? (this.isBigNumber ? new BigNumber(0) : 0) : this.faultBackValue) as TModelType;
    await this.setValueAsync(resetValue);
  }

  setDefaultValueFromLayoutMetaData(defaultValue: string): void {
    if (typeof defaultValue === 'number') {
      this._layoutDefaultValue = defaultValue;
    }
  }

  static getMaskedFormattedValue(propertyViewModel: BaseNumericPropertyViewModel<any>): string {
    const maskSettings = BaseNumericPropertyViewModel.getMaskSettings(propertyViewModel);
    const formattedValue = NumericHelper.getFormattedValue(propertyViewModel.value, maskSettings);
    return formattedValue;
  }

  static getMaskSettings(propertyViewModel: BaseNumericPropertyViewModel<any>): NumericMaskSettings {
    const nullable = propertyViewModel.isNullable;
    const digits = NumericHelper.getDecimalDigits(propertyViewModel.propertyName, propertyViewModel.integerLimit, propertyViewModel.decimalLimit);
    let integerDigits = NumericHelper.getIntegerDigits(propertyViewModel.propertyName, propertyViewModel.integerLimit, propertyViewModel.decimalLimit);
    if (propertyViewModel.isDecimal === false && integerDigits > NumericHelper.MAX_LENGTH_INT32) {
      integerDigits = NumericHelper.MAX_LENGTH_INT32;
    }

    const max = propertyViewModel.maxValue != null ? propertyViewModel.maxValue : NumericHelper.calculateMaxValue(digits, integerDigits, propertyViewModel.isDecimal);
    const min = propertyViewModel.minValue != null ? propertyViewModel.minValue : NumericHelper.calculateMinValue(digits, integerDigits, propertyViewModel.isDecimal);

    const isBigNumber = (integerDigits + digits) >= NumericHelper.MIN_DIGIT_FOR_BIGNUMBER;

    const defaultMaskSettings: NumericMaskSettings = {
      nullable,
      decimalLimit: digits,
      integerLimit: integerDigits,
      useThousandSeparator: propertyViewModel.useThousandSeparator,
      min,
      max,
      isBigNumber,
      normalizeZeros: true,
      padFractionalZeros: true,
      faultBackValueFunc: () => propertyViewModel.faultBackValue?.toString() ?? ''
    };

    if (propertyViewModel.customMaskSettings != null) {
      return propertyViewModel.customMaskSettings(
        defaultMaskSettings
      );
    } else {
      return defaultMaskSettings;
    }
  }
}

